import React from 'react';
import { useState } from 'react';
import FirebaseService from '../services/FirebaseService';
import CertificateDetails from '../components/certificate/CertificateDetails';

const Verify = () => {
  const [tabValue, setTabValue] = useState(0); // 0 for certificate, 1 for company
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [certificate, setCertificate] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [error, setError] = useState('');

  const handleTabChange = (newValue) => {
    setTabValue(newValue);
    setCertificate(null);
    setCertificates([]);
    setError('');
    setSearchTerm('');
  };

  const handleSearch = async () => {
    const trimmedSearchTerm = searchTerm.trim();
    
    if (!trimmedSearchTerm) {
      setError('Please enter a search term');
      return;
    }
    
    // Require at least 3 characters for search to prevent data exposure
    if (trimmedSearchTerm.length < 3) {
      setError('Please enter at least 3 characters for search');
      return;
    }

    setLoading(true);
    setError('');
    setCertificate(null);
    setCertificates([]);

    try {
      if (tabValue === 0) { // Certificate Number search
        const result = await FirebaseService.getCertificateByNumber(searchTerm.trim());
        if (result) {
          setCertificate(result);
        } else {
          setError('No certificate found with the provided number');
        }
      } else { // Company Name search
        const results = await FirebaseService.searchCertificatesByCompany(searchTerm.trim());
        if (results.length > 0) {
          setCertificates(results);
        } else {
          setError('No certificates found for the provided company name');
        }
      }
    } catch (err) {
      // Error searching certificates
      setError('An error occurred while searching. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading) {
      handleSearch();
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#000000',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '0 16px 40px',
      fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
    }}>
      {/* Main Card */}
      <div style={{
        width: '100%',
        maxWidth: '820px',
      }}>
        <div style={{ padding: '32px 0 0' }}>
          {/* Segmented toggle */}
          <div style={{
            display: 'inline-flex',
            backgroundColor: '#1a1a1a',
            borderRadius: '10px',
            padding: '4px',
            marginBottom: '28px',
            gap: '2px',
          }}>
            {[
              { label: 'Certificate No.', icon: '🔎' },
              { label: 'Company Name', icon: '🏢' },
            ].map(({ label, icon }, index) => (
              <button
                key={index}
                onClick={() => handleTabChange(index)}
                style={{
                  background: tabValue === index ? '#c9a227' : 'transparent',
                  border: 'none',
                  borderRadius: '7px',
                  color: tabValue === index ? '#000000' : '#888888',
                  fontSize: '13.5px',
                  fontWeight: tabValue === index ? '700' : '400',
                  padding: '9px 20px',
                  cursor: 'pointer',
                  outline: 'none',
                  transition: 'all 0.18s',
                  boxShadow: tabValue === index ? '0 1px 6px rgba(201,162,39,0.4)' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  whiteSpace: 'nowrap',
                }}
              >
                <span style={{ fontSize: '13px' }}>{icon}</span>
                {label}
              </button>
            ))}
          </div>

          {/* Search Row */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '8px' }}>
            <input
              type="text"
              placeholder={tabValue === 0 ? 'Enter Certificate Number' : 'Enter Company Name'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={loading}
              style={{
                flex: 1,
                height: '52px',
                border: '1.5px solid #333333',
                borderRadius: '8px',
                padding: '0 18px',
                fontSize: '15px',
                outline: 'none',
                color: '#ffffff',
                backgroundColor: '#1a1a1a',
                boxSizing: 'border-box',
                transition: 'border-color 0.2s',
              }}
              onFocus={e => e.target.style.borderColor = '#c9a227'}
              onBlur={e => e.target.style.borderColor = '#333333'}
            />
            <button
              onClick={handleSearch}
              disabled={loading}
              style={{
                height: '52px',
                minWidth: '130px',
                background: loading ? '#444444' : '#c9a227',
                color: loading ? '#888888' : '#000000',
                border: 'none',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: '700',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '0 24px',
                boxShadow: loading ? 'none' : '0 4px 14px rgba(201,162,39,0.35)',
                transition: 'all 0.2s',
                letterSpacing: '0.2px',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              Search
            </button>
          </div>

          {/* Error */}
          {error && (
            <p style={{ color: '#ff6b6b', fontSize: '13.5px', margin: '12px 0 0 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              {error}
            </p>
          )}

          {/* Single Certificate Result */}
          {certificate && <CertificateDetails certificate={certificate} />}

          {/* Multiple Certificate Results */}
          {certificates.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <p style={{ fontSize: '13px', color: '#888888', marginBottom: '8px', fontWeight: '500' }}>
                Found {certificates.length} certificate{certificates.length !== 1 ? 's' : ''}
              </p>
              {certificates.map((cert, index) => (
                <CertificateDetails key={cert.id || index} certificate={cert} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Verify;
